import { useState, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Receipt, X, Upload, Check, Loader2, ArrowRight, ArrowLeft, AlertCircle, FileText } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManualPaymentModal({ open, onClose, negocioId, onSaved }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";

  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState("");
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [form, setForm] = useState({ valor: "", data_pagamento: "", forma_pagamento: "", banco: "", nome_pagador: "", nome_recebedor: "", observacao: "" });
  const [saving, setSaving] = useState(false);

  const reset = () => { setStep(0); setFile(null); setFileId(""); setAiResult(null); setForm({ valor: "", data_pagamento: "", forma_pagamento: "", banco: "", nome_pagador: "", nome_recebedor: "", observacao: "" }); };

  const handleUpload = async (f) => {
    setFile(f);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("negocio_id", negocioId);
      const res = await axios.post(`${API}/files/upload`, fd);
      setFileId(res.data.id);
      toast.success("Comprovante enviado!");
    } catch { toast.error("Erro ao enviar comprovante."); }
    setUploading(false);
  };

  const handleValidate = async () => {
    if (!fileId) { toast.error("Envie o comprovante primeiro."); return; }
    setValidating(true);
    try {
      const res = await axios.post(`${API}/asaas/validate-receipt`, { file_ids: [fileId] });
      if (res.data.ok && res.data.data) {
        const d = res.data.data;
        setAiResult(d);
        setForm({
          valor: d.valor || "",
          data_pagamento: d.data_pagamento || "",
          forma_pagamento: d.forma_pagamento || "",
          banco: d.banco || "",
          nome_pagador: d.nome_pagador || "",
          nome_recebedor: d.nome_recebedor || "",
          observacao: d.observacao || "",
        });
        if (d.approved) { setStep(1); }
        else { toast.error(d.reason || "Comprovante rejeitado pela IA."); }
      } else {
        toast.error("Erro na validação.");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erro na análise."); }
    setValidating(false);
  };

  const handleSave = async () => {
    if (!form.valor || parseFloat(form.valor) <= 0) { toast.error("Informe o valor."); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/asaas/manual-payment`, {
        negocio_id: negocioId,
        ...form,
        valor: parseFloat(form.valor),
        file_ids: [fileId],
        ai_validation: aiResult,
        description: `Pagamento manual - ${form.forma_pagamento || "Comprovante"}`,
      });
      if (res.data.ok) {
        toast.success("Pagamento registrado!");
        if (onSaved) onSaved();
        reset();
        onClose();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao registrar."); }
    setSaving(false);
  };

  const fmtValue = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: appearance.default_currency || "BRL" }).format(v || 0);

  if (!open) return null;

  const STEPS = [{ n: 1, l: "Comprovante" }, { n: 2, l: "Conferência" }, { n: 3, l: "Resumo" }];

  return createPortal(
    <div className="fixed inset-0 z-[99998] bg-black/60 flex items-center justify-center">
      <div className="rounded-lg shadow-2xl w-full max-w-lg overflow-hidden" style={{ backgroundColor: "#f8fafc" }}>
        <div className="px-6 pt-5 pb-3">
          <div className="flex items-center justify-between mb-5">
            <p className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><Receipt className="w-5 h-5" style={{ color: tc500 }} />Registrar Pagamento</p>
            <button onClick={() => { reset(); onClose(); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center w-full px-4">
            {STEPS.map((s, idx) => (
              <div key={s.n} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center" style={{ width: "60px" }}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step >= idx ? "text-white" : "bg-slate-100 text-slate-400"}`} style={step >= idx ? { backgroundColor: tc500 } : undefined}>
                    {step > idx ? <Check className="w-4 h-4" /> : s.n}
                  </div>
                  <span className={`text-[10px] font-medium mt-1 ${step >= idx ? "text-slate-800" : "text-slate-400"}`}>{s.l}</span>
                </div>
                {idx < STEPS.length - 1 && <div className={`h-px flex-1 -mt-4 ${step > idx ? "" : "bg-slate-200"}`} style={step > idx ? { backgroundColor: tc500 } : undefined} />}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6 flex flex-col" style={{ minHeight: "300px" }}>
          {/* Step 1: Upload comprovante */}
          {step === 0 && (
            <div className="space-y-4 flex-1 pt-4">
              <p className="text-sm text-slate-600">Envie o comprovante de pagamento (imagem ou PDF). A IA irá extrair as informações automaticamente.</p>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-slate-400 transition-colors cursor-pointer"
                onClick={() => document.getElementById("receipt-upload").click()}>
                <input id="receipt-upload" type="file" accept="image/*,.pdf" className="hidden" onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); }} />
                {!file ? (
                  <><Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" /><p className="text-sm text-slate-500">Clique para enviar o comprovante</p><p className="text-[10px] text-slate-400">Imagem ou PDF</p></>
                ) : (
                  <div className="flex items-center gap-3 justify-center">
                    <FileText className="w-5 h-5 text-slate-500" />
                    <span className="text-sm text-slate-700">{file.name}</span>
                    {uploading && <Loader2 className="w-4 h-4 animate-spin" style={{ color: tc500 }} />}
                    {fileId && <Check className="w-4 h-4 text-emerald-500" />}
                  </div>
                )}
              </div>
              {aiResult && !aiResult.approved && (
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{aiResult.reason || "Comprovante rejeitado. Envie outro."}
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={!fileId || uploading || validating} onClick={handleValidate}>
                  {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  <span className="flex items-center gap-1">Analisar com IA<ArrowRight className="w-4 h-4" /></span>
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Conferência dos dados */}
          {step === 1 && (
            <div className="space-y-3 flex-1 pt-4">
              {aiResult?.approved && (
                <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700 flex items-center gap-2">
                  <Check className="w-3.5 h-3.5" />Comprovante aprovado pela IA
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Valor</Label>
                  <Input type="number" step="0.01" value={form.valor} onChange={e => setForm(f => ({ ...f, valor: e.target.value }))} style={{ backgroundColor: "#fff" }} className="mt-1 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Data do pagamento</Label>
                  <Input type="date" value={form.data_pagamento} onChange={e => setForm(f => ({ ...f, data_pagamento: e.target.value }))} style={{ backgroundColor: "#fff" }} className="mt-1 h-9 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Forma de pagamento</Label>
                  <Input value={form.forma_pagamento} onChange={e => setForm(f => ({ ...f, forma_pagamento: e.target.value }))} style={{ backgroundColor: "#fff" }} className="mt-1 h-9 text-sm" placeholder="Pix, TED, Boleto..." />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700">Banco</Label>
                  <Input value={form.banco} onChange={e => setForm(f => ({ ...f, banco: e.target.value }))} style={{ backgroundColor: "#fff" }} className="mt-1 h-9 text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Nome do pagador</Label>
                <Input value={form.nome_pagador} onChange={e => setForm(f => ({ ...f, nome_pagador: e.target.value }))} style={{ backgroundColor: "#fff" }} className="mt-1 h-9 text-sm" />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Observação</Label>
                <Input value={form.observacao} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} style={{ backgroundColor: "#fff" }} className="mt-1 h-9 text-sm" />
              </div>
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(0)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={!form.valor} onClick={() => setStep(2)}>
                  <span className="flex items-center gap-1">Avançar<ArrowRight className="w-4 h-4" /></span>
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Resumo */}
          {step === 2 && (
            <div className="space-y-3 flex-1 pt-4">
              <div className="border border-slate-200 rounded-lg p-4 space-y-2" style={{ backgroundColor: "#fff" }}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Valor</span>
                  <span className="text-lg font-bold text-slate-900">{fmtValue(form.valor)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Data</span>
                  <span className="text-sm font-medium text-slate-800">{form.data_pagamento ? new Date(form.data_pagamento + "T12:00:00").toLocaleDateString("pt-BR") : "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Forma</span>
                  <span className="text-sm text-slate-700">{form.forma_pagamento || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Banco</span>
                  <span className="text-sm text-slate-700">{form.banco || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">Pagador</span>
                  <span className="text-sm text-slate-700">{form.nome_pagador || "—"}</span>
                </div>
                {form.observacao && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">Observação</span>
                    <span className="text-sm text-slate-600">{form.observacao}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(1)} className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100"><ArrowLeft className="w-4 h-4" /></button>
                <Button className="text-white" style={{ backgroundColor: tc500 }} disabled={saving} onClick={handleSave}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Confirmar pagamento
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
